import { t } from "@lingui/core/macro";
import { AlertMessage, ColumnProps } from "../../../api/types";
import DataTable from "../../dataTable";
import { Box, Pagination } from "@mui/material";
import React, { useEffect, useState } from "react";
import { usePostQuery } from "../../../hooks/usePostQuery";
import TaxonomyDialog from "./taxonomyDialog";
import { Apifilters, dimensionNs, Label } from "../../../api/customObjectsTypes";

export const TaxonomyDimensionValues = ({
    id,
    setAlertMessage,
    setTotal,
}: {
    id: string;
    setAlertMessage: (alertMessage: AlertMessage) => void
    setTotal: (total: number) => void;
}) => {

    const initReq = {
        page: 1,
        pageCount: 20,
        filters: [
            { column: "dimension_name", filterType: 1, ColumnType: 0, sortDescending: false },
            { column: "value", filterType: 1, ColumnType: 0, sortDescending: false },
            { column: "isTypedMember", filterType: 0, ColumnType: "bool", sortDescending: false },
            { column: "isDefault", filterType: 0, ColumnType: 1, sortDescending: false },
            { column: "isCustom", filterType: 0, ColumnType: "bool", sortDescending: false },
        ],
        taxonomyId: id,
    };

    interface TaxonomyDimensionValue {
        totalCount: number;
        result: {
            value: string;
            isTypedMember: boolean;
            isDefault: boolean;
            taxonomyId: string;
            isCustom: boolean;
            relativePath: string;
            ns: {
                abbreviation: string;
                href: string;
                id: string;
            };
            dimension: {
                name: string;
                ns: dimensionNs;
                isHypercube: boolean;
                relativePath: string;
            };
            labels: Label[];
            id: string;
        }[];
        filters: Apifilters[];
    }

    const [shouldFetch, setShouldFetch] = useState(true);
    const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
    const [cacheBuster] = useState(crypto.randomUUID());
    const [req, setReq] = useState(() => (initReq));

    const { data, loading } = usePostQuery<TaxonomyDimensionValue>(
        `api/taxonomies/${id}/dimension-values/search?cb=${cacheBuster}`,
        req,
        !shouldFetch
    );

    useEffect(() => {
        if (data) {
            setTotal(data.totalCount);
        }
    }, [data, setTotal]);

    useEffect(() => {
        if (shouldFetch) {
            setShouldFetch(false);
        }
    }, [shouldFetch]);

    const Tablecolumns: () => ColumnProps[] = () => {
        return [
            {
                header: {
                    id: "dimension_name",
                    label: t`Dimension`,
                    optionsHeaderText: t`Dimension`,
                    shouldBeFiltered: true,
                    inputType: "text",
                    width: 600,
                },
                valueFormatter: (row: any) => row?.dimension?.name || "",
                key: "dimension_name",
                width: 600,
            },
            {
                header: {
                    id: "value",
                    label: t`Value`,
                    shouldBeFiltered: true,
                    optionsHeaderText: t`Select Value`,
                    inputType: "text",
                    width: 150,
                },
                key: "value",
                width: 150,
            },
            {
                header: {
                    id: "isTypedMember",
                    label: t`Is Typed Member`,
                    shouldBeFiltered: true,
                    optionsHeaderText: t`Select Is Typed Member`,
                    inputType: "select",
                    width: 250,
                },
                valueFormatter: (row: any) => row?.isTypedMember ? t`Yes` : t`No`,
                key: "isTypedMember",
                width: 250,
            },
            {
                header: {
                    id: "isDefault",
                    label: t`Is Default`,
                    shouldBeFiltered: true,
                    inputType: "select",
                    width: 250,
                },
                valueFormatter: (row: any) => row?.isDefault ? t`Yes` : t`No`,
                key: "isDefault",
                width: 250,
            },
            {
                header: {
                    id: "isCustom",
                    label: t`Is Custom`,
                    shouldBeFiltered: true,
                    inputType: "select",
                    width: 250,
                },
                valueFormatter: (row: any) => row?.isCustom ? t`True` : t`False`,
                key: "isCustom",
                width: 250,
            },
        ];
    };

    const handleSetFilter = (columnId: string, value?: string | boolean | number | null, sortDescending?: boolean, enterPressed?: boolean) => {
        if (columnId === "page") {
            setReq((prevReq: any) => {
                return {
                    ...prevReq,
                    page: value,
                };
            });
        } else {

            setReq((prevReq: any) => {
                return {
                    ...prevReq,
                    filters: prevReq.filters.map((filter: any) => {
                        if (filter.column === columnId) {
                            return { ...filter, value: value === "null" ? null : value === "empty" ? "" : value, sortDescending };
                        }
                        return filter;
                    }),
                };
            });
        }
        if (enterPressed) {
            setShouldFetch(true);
        }
    };

    useEffect(() => {
        const hasSortDescendingChanged = req.filters.some((filter: Apifilters) => filter.sortDescending !== undefined);
        if (hasSortDescendingChanged) {
            setReq((prevReq: any) => {
                const newFilters = prevReq.filters.map((filter: Apifilters) => ({
                    ...filter,
                    sortDescending: filter.sortDescending,
                }));
                const isSame = JSON.stringify(prevReq.filters) === JSON.stringify(newFilters);
                if (isSame) return prevReq;
                return {
                    ...prevReq,
                    filters: newFilters,
                    page: 1,
                };
            });
        }
    }, [req.filters]);

    return (
        <>
            <Box sx={{ mt: 5, alignContent: "center", display: "flex", flexDirection: "column" }}>
                <DataTable
                    data={data?.result || []}
                    loading={loading}
                    filters={data?.filters || []}
                    setFilter={handleSetFilter}
                    columns={Tablecolumns()}
                    noWidth
                    noDataMessage={t`No Taxonomy Elements`}
                    onClickRow={(row) => {
                        setSelectedId(row);
                    }}
                />
                {data && data?.totalCount > 20 && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 2 }}>
                        <Pagination
                            count={Math.ceil(data.totalCount / 20)}
                            page={req.page}
                            onChange={(event, value) => handleSetFilter("page", value, undefined, true)}
                            color="primary"
                        />
                    </Box>
                )}
            </Box>
            <TaxonomyDialog
                selectTemplate="taxonomyDimensionValue"
                open={!!selectedId}
                onClose={(updateStatus, reason) => {
                    setSelectedId(undefined);
                    if (updateStatus) {
                        setAlertMessage({ message: reason || "", severity: updateStatus });
                        setShouldFetch(true);
                    }
                }}
                parentId={id}
                id={selectedId || ""}
            />
        </>)
};